import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowForwardRounded as ArrowForwardIcon,
  CheckCircleRounded as CheckCircleIcon,
  EventAvailableRounded as EventAvailableIcon,
  GroupRounded as GroupIcon,
  HourglassEmptyRounded as HourglassEmptyIcon,
  PersonOffRounded as PersonOffIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { motion, useReducedMotion } from 'framer-motion';
import { toast } from 'react-toastify';

import api from '../../services/api';
import { fetchHrRequests } from '../../services/allRequestsApi';
import AnnouncementAlertBanner from '../../components/Dashboard/AnnouncementAlertBanner';
import { useMotionPresets } from '../../theme/motion';
import {
  daysUntilDate,
  formatDateOnly,
  isAnnouncementActive,
  isHolidayUpcoming,
} from '../../utils/notificationUtils';
import { EmptyState, LoadingState, PageLayout, StatusBadge } from '../../components/ui';
import { createRoleCapabilities, parseStoredList } from '../../utils/roleCapabilities';
import {
  CoverageRing,
  ManagerKpiTile,
  RequestReviewCard,
  TeamWeekStrip,
} from '../../components/manager/ManagerVisuals';
import {
  computeTeamWeek,
  findOverlaps,
  getEmployeeCode,
  getLeaveTypeLabel,
  getRequestRange,
  isPendingStatus,
  normalizeLeaveStatus,
} from '../../utils/leaveVisuals';

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.data)) return value.data;
  if (value && Array.isArray(value.items)) return value.items;
  return [];
}

function numberOr(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function dayStart(value) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function daysSince(value) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return Math.floor((Date.now() - parsed.getTime()) / 86400000);
}

function calculateServicePeriod(join) {
  if (!join) return 'N/A';
  const start = new Date(join);
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  let days = now.getDate() - start.getDate();
  if (days < 0) {
    months -= 1;
    days += 30;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return `${years}y ${months}m ${days}d`;
}

function daysRemainingInYear() {
  const today = new Date();
  return Math.ceil((new Date(today.getFullYear(), 11, 31, 23, 59, 59) - today) / 86400000);
}

function getDailyAccrualRate(contract, type) {
  const baseType = String(type || '').replace(/\(.*?\)/g, '').trim();
  if (baseType !== 'Annual') return 0;
  if (contract === '30 Days Yearly') return 30 / 365;
  if (contract === '24 Days Yearly') return 24 / 365;
  if (contract === '21 Days Yearly') return 21 / 365;
  if (contract === '20 Days Yearly') return 20 / 365;
  return 0;
}

function getInitials(name) {
  return String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'M';
}

function getSortTimestamp(item) {
  return new Date(item?.created_at || item?.updated_at || item?.start_date || item?.date_from || 0).getTime();
}

function latest(rows, count = 5) {
  return asArray(rows)
    .slice()
    .sort((left, right) => getSortTimestamp(right) - getSortTimestamp(left))
    .slice(0, count);
}

function getRequestTypeDisplay(request) {
  return request?.title || getLeaveTypeLabel(request?.leave_type) || request?.type || request?.request_type || '-';
}

function getDateRangeLabel(request) {
  const start = formatDateOnly(request?.start_date || request?.date_from) || '-';
  const end = formatDateOnly(request?.end_date || request?.date_to) || '-';
  return `${start} - ${end}`;
}

function ManagerPanel({ title, subtitle, action, children, sx }) {
  const theme = useTheme();
  const motionPresets = useMotionPresets();
  return (
    <Paper
      component={motion.div}
      variant="outlined"
      {...motionPresets.revealOnScroll}
      sx={{
        borderRadius: '12px',
        borderColor: theme.hr?.line?.soft || 'divider',
        backgroundColor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.78)
            : alpha(theme.palette.background.paper, 0.96),
        boxShadow: 'none',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {title || action ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'flex-start' }}
          justifyContent="space-between"
          sx={{ px: { xs: 1.3, sm: 1.5 }, pt: { xs: 1.3, sm: 1.5 }, pb: 0.8 }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title ? (
              <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                {title}
              </Typography>
            ) : null}
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {action}
        </Stack>
      ) : null}
      <Box sx={{ px: { xs: 1.3, sm: 1.5 }, pb: { xs: 1.3, sm: 1.5 }, pt: title || action ? 0 : { xs: 1.3, sm: 1.5 } }}>
        {children}
      </Box>
    </Paper>
  );
}

function BalanceRow({ balance }) {
  const theme = useTheme();
  const current = numberOr(balance.current_balance);
  const projected = numberOr(balance.projected_balance, current);
  const max = Math.max(current, projected, 1);
  const pct = Math.max(0, Math.min(100, (current / max) * 100));
  const label = getLeaveTypeLabel(balance.leave_type);
  return (
    <Box
      sx={{
        p: 1,
        borderRadius: '10px',
        border: '1px solid',
        borderColor: theme.hr?.line?.soft || 'divider',
        backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.03) : alpha(theme.palette.primary.main, 0.025),
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Projected {projected.toFixed(1)} days
          </Typography>
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
          {current.toFixed(1)}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ mt: 0.9, height: 6, borderRadius: 999, bgcolor: alpha(theme.palette.primary.main, 0.08), '& .MuiLinearProgress-bar': { borderRadius: 999 } }}
      />
    </Box>
  );
}

function RecentRequestsTable({ rows }) {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));
  if (!rows.length) {
    return <EmptyState compact title="No requests" description="No recent submissions." />;
  }
  if (isCompact) {
    return (
      <Stack spacing={0.8}>
        {rows.map((request, index) => (
          <Paper key={request.id || index} variant="outlined" sx={{ p: 1, borderRadius: '10px' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 850 }} noWrap>
                  {getRequestTypeDisplay(request)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getDateRangeLabel(request)}
                </Typography>
              </Box>
              <StatusBadge status={request.status_label || request.status || '-'} />
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  }
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px' }}>
      <Table size="small">
        <TableHead>
          <TableRow
            sx={{ '& .MuiTableCell-head': { fontWeight: 800, color: 'text.secondary', backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}
          >
            <TableCell>Type</TableCell>
            <TableCell>Window</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((request, index) => (
            <TableRow key={request.id || index} hover>
              <TableCell>{getRequestTypeDisplay(request)}</TableCell>
              <TableCell>{getDateRangeLabel(request)}</TableCell>
              <TableCell>
                <StatusBadge status={request.status_label || request.status || '-'} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ManagerCommandBar({
  manager,
  roleLabel,
  subtitle,
  teamPending,
  oldestWaitDays,
  coveragePct,
  teamAvailable,
  teamSize,
  onRefresh,
  onNewRequest,
  onQueue,
}) {
  const theme = useTheme();
  const reduced = useReducedMotion();
  const dark = theme.palette.mode === 'dark';
  const allClear = teamPending === 0;

  return (
    <Box
      component={motion.div}
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduced ? 0.01 : 0.45, ease: [0.2, 0, 0, 1] }}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '16px',
        p: { xs: 1.6, sm: 2 },
        color: '#ffffff',
        border: '1px solid',
        borderColor: alpha('#ffffff', dark ? 0.16 : 0.22),
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${alpha(
          theme.palette.secondary.main,
          0.92
        )} 100%)`,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `radial-gradient(640px 200px at 12% -10%, ${alpha('#ffffff', 0.18)}, transparent 60%)`,
        }}
      />
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ position: 'relative' }}>
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              fontWeight: 800,
              backgroundColor: alpha('#ffffff', 0.18),
            }}
          >
            {getInitials(manager?.name)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 850, lineHeight: 1.1 }} noWrap>
              {manager?.name ? `Welcome back, ${manager.name}` : `${roleLabel} workspace`}
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#ffffff', 0.8) }} noWrap>
              {subtitle}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} sx={{ color: '#ffffff', backgroundColor: alpha('#ffffff', 0.14), '&:hover': { backgroundColor: alpha('#ffffff', 0.24) } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onNewRequest}
            sx={{ backgroundColor: alpha('#ffffff', 0.16), boxShadow: 'none', '&:hover': { backgroundColor: alpha('#ffffff', 0.24), boxShadow: 'none' } }}
          >
            New request
          </Button>
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ position: 'relative', mt: { xs: 1.6, sm: 1.4 } }}
      >
        <Stack direction="row" spacing={1.6} alignItems="center" sx={{ minWidth: 0 }}>
          {allClear ? (
            <CheckCircleIcon sx={{ fontSize: 52, color: alpha('#ffffff', 0.92) }} />
          ) : (
            <Typography sx={{ fontSize: 54, fontWeight: 900, lineHeight: 0.9 }}>{teamPending}</Typography>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 850, lineHeight: 1.12 }}>
              {allClear ? "You're all caught up" : `request${teamPending === 1 ? '' : 's'} need your decision`}
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#ffffff', 0.8), display: 'block', mb: 1 }}>
              {allClear
                ? 'No team requests are waiting on you.'
                : oldestWaitDays != null
                  ? `Oldest has been waiting ${oldestWaitDays} day${oldestWaitDays === 1 ? '' : 's'}.`
                  : 'Review and respond to keep your team moving.'}
            </Typography>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={onQueue}
              sx={{ backgroundColor: '#ffffff', color: theme.palette.primary.dark, boxShadow: 'none', '&:hover': { backgroundColor: alpha('#ffffff', 0.88), boxShadow: 'none' } }}
            >
              {allClear ? 'Go to queue' : 'Review queue'}
            </Button>
          </Box>
        </Stack>

        <Box sx={{ color: '#ffffff', alignSelf: { xs: 'flex-start', sm: 'center' } }}>
          <CoverageRing
            value={coveragePct}
            size={108}
            color={theme.palette.success.light || '#5DCAA5'}
            trackColor={alpha('#ffffff', 0.26)}
            secondaryLabel={teamSize ? `${teamAvailable} of ${teamSize} in` : 'No team'}
          />
        </Box>
      </Stack>
    </Box>
  );
}

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const role = localStorage.getItem('role') || 'manager';
  const roleCapabilities = useMemo(
    () =>
      createRoleCapabilities({
        role,
        actualRole: localStorage.getItem('actual_role') || '',
        permissions: parseStoredList('permissions'),
        isPlatformAdmin: localStorage.getItem('is_platform_admin') === '1',
      }),
    [role]
  );

  const vacationBase = roleCapabilities.hasFinanceAccess
    ? '/finance'
    : roleCapabilities.hasExecutiveFamilyAccess
      ? '/ceo'
      : '/manager';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [manager, setManager] = useState(null);
  const [contractType, setContractType] = useState('Unknown');
  const [vacations, setVacations] = useState([]);
  const [myReqs, setMyReqs] = useState([]);
  const [recentReqs, setRecentReqs] = useState([]);
  const [teamRequests, setTeamRequests] = useState([]);
  const [teamSummary, setTeamSummary] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [anns, setAnns] = useState([]);
  const [actingId, setActingId] = useState(null);
  const [preApprovals, setPreApprovals] = useState([]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const endpoint = `${vacationBase}/dashboard`;
      const [dashboardResponse, teamResponse, holidayResponse, announcementResponse, preApprovalResponse] = await Promise.all([
        api.get(endpoint),
        api.get(`${vacationBase}/vacation-requests`, { params: { paginate: false } }).catch(() => ({ data: [] })),
        api.get('/holidays?future=1&active=1'),
        api.get('/announcements'),
        roleCapabilities.hasPreApprovalAccess
          ? fetchHrRequests({ request_type: 'pre_approval', view_mode: 'assigned_requests', queue: 'pending' }).catch(() => [])
          : Promise.resolve([]),
      ]);

      setManager(dashboardResponse.data.manager);
      setContractType(dashboardResponse.data.manager?.contract_type || 'Unknown');
      setVacations(asArray(dashboardResponse.data.vacations));
      setMyReqs(asArray(dashboardResponse.data.managerRequests));
      setRecentReqs(asArray(dashboardResponse.data.recent_requests));
      setTeamSummary(dashboardResponse.data.team_summary || {});
      const fullTeam = asArray(teamResponse.data);
      setTeamRequests(fullTeam.length ? fullTeam : asArray(dashboardResponse.data.subordinateRequests));
      setHolidays(asArray(holidayResponse.data));
      setAnns(asArray(announcementResponse.data));
      const preApprovalRows = Array.isArray(preApprovalResponse)
        ? preApprovalResponse
        : asArray(preApprovalResponse?.data);
      setPreApprovals(preApprovalRows);
    } catch (requestError) {
      console.error(requestError);
      setError(requestError?.response?.data?.message || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, roleCapabilities.hasExecutiveFamilyAccess, roleCapabilities.hasFinanceAccess]);

  async function handleDecision(request, action) {
    if (!request?.id || actingId) return;
    setActingId(request.id);
    try {
      await api.patch(`${vacationBase}/vacation-requests/${request.id}`, { status: action });
      toast.success(`Request ${action === 'Approved' ? 'approved' : 'declined'}.`);
      setTeamRequests((previous) =>
        previous.map((row) => (row.id === request.id ? { ...row, status: action } : row))
      );
      if (typeof window.refreshAssignedPendingVacationCount === 'function') {
        window.refreshAssignedPendingVacationCount();
      }
      if (typeof window.refreshAssignedPendingCount === 'function') {
        window.refreshAssignedPendingCount();
      }
    } catch (decisionError) {
      console.error(decisionError);
      toast.error(decisionError?.response?.data?.message || 'Could not update request.');
    } finally {
      setActingId(null);
    }
  }

  const balances = useMemo(
    () =>
      asArray(vacations).map((balance) => {
        const currentBalance = numberOr(balance.current_balance);
        const dailyRate = getDailyAccrualRate(contractType, balance.leave_type);
        return {
          ...balance,
          current_balance: currentBalance,
          projected_balance: currentBalance + dailyRate * daysRemainingInYear(),
        };
      }),
    [vacations, contractType]
  );

  const today = useMemo(() => dayStart(new Date()), []);

  const teamPendingList = useMemo(
    () =>
      asArray(teamRequests)
        .filter((row) => isPendingStatus(row.status))
        .sort((left, right) => getSortTimestamp(left) - getSortTimestamp(right)),
    [teamRequests]
  );

  const approvedTeam = useMemo(
    () => asArray(teamRequests).filter((row) => normalizeLeaveStatus(row.status) === 'approved'),
    [teamRequests]
  );

  const teamSize = numberOr(teamSummary?.total_subordinates, 0) || (() => {
    const codes = new Set(asArray(teamRequests).map((row) => getEmployeeCode(row)).filter(Boolean));
    return codes.size;
  })();

  const outTodayList = useMemo(() => {
    const seen = new Map();
    approvedTeam.forEach((row) => {
      const { start, end } = getRequestRange(row);
      if (start && end && today && start <= today && end >= today) {
        const code = getEmployeeCode(row) || row.id;
        if (!seen.has(code)) seen.set(code, row);
      }
    });
    return Array.from(seen.values());
  }, [approvedTeam, today]);

  const teamOutToday = outTodayList.length;
  const teamAvailable = Math.max(numberOr(teamSize) - teamOutToday, 0);
  const coveragePct = teamSize ? Math.round((teamAvailable / teamSize) * 100) : 100;

  const teamUpcoming30 = useMemo(() => {
    if (!today) return 0;
    const horizon = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 30);
    return approvedTeam.filter((row) => {
      const { start } = getRequestRange(row);
      return start && start > today && start <= horizon;
    }).length;
  }, [approvedTeam, today]);

  const teamPending = teamPendingList.length;
  const oldestWaitDays = useMemo(() => {
    if (!teamPendingList.length) return null;
    return teamPendingList.reduce((max, row) => {
      const age = daysSince(row.created_at || row.updated_at);
      return age != null && age > max ? age : max;
    }, 0);
  }, [teamPendingList]);

  const teamWeek = useMemo(
    () => computeTeamWeek(teamRequests, teamSize, new Date()),
    [teamRequests, teamSize]
  );

  const recentMyRequests = useMemo(() => latest(recentReqs.length ? recentReqs : myReqs, 5), [myReqs, recentReqs]);
  const activeAnnouncements = useMemo(() => asArray(anns).filter((a) => isAnnouncementActive(a)), [anns]);
  const upcomingHolidays = useMemo(
    () =>
      asArray(holidays)
        .filter((holiday) => isHolidayUpcoming(holiday))
        .slice()
        .sort((left, right) => new Date(left.holiday_date) - new Date(right.holiday_date)),
    [holidays]
  );
  const nextTeamLeave = teamSummary?.next_team_leave || null;

  const kpis = useMemo(
    () => [
      {
        tone: 'warning',
        icon: <HourglassEmptyIcon fontSize="small" />,
        label: 'Pending',
        value: teamPending,
        hint: 'Awaiting your decision',
        onClick: () => navigate('/manager/vacation-requests'),
      },
      {
        tone: 'info',
        icon: <PersonOffIcon fontSize="small" />,
        label: 'Away today',
        value: teamOutToday,
        hint: `${teamAvailable} available`,
      },
      {
        tone: 'secondary',
        icon: <GroupIcon fontSize="small" />,
        label: 'Leave in 30d',
        value: teamUpcoming30,
        hint: 'Approved & upcoming',
      },
      {
        tone: 'success',
        icon: <EventAvailableIcon fontSize="small" />,
        label: 'Coverage',
        value: `${coveragePct}%`,
        hint: 'Available today',
      },
    ],
    [teamPending, teamOutToday, teamAvailable, teamUpcoming30, coveragePct, navigate]
  );

  if (loading) {
    return (
      <PageLayout backgroundAccent compact>
        <LoadingState rows={9} />
      </PageLayout>
    );
  }

  const roleLabel = roleCapabilities.hasCeoAccess
    ? 'CEO'
    : roleCapabilities.hasExecutiveAccess
      ? 'Executive'
      : roleCapabilities.hasFinanceAccess
        ? 'Finance'
        : roleCapabilities.hasHodAccess
          ? 'HOD'
          : roleCapabilities.hasOperationsAccess
            ? 'Operations'
            : 'Manager';

  const subtitle = [
    manager?.position || roleLabel,
    manager?.department || null,
    teamSize ? `${teamSize} in team` : null,
    `Service ${calculateServicePeriod(manager?.joining_date)}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <PageLayout backgroundAccent compact>
      <Stack spacing={1.4}>
        <ManagerCommandBar
          manager={manager}
          roleLabel={roleLabel}
          subtitle={subtitle}
          teamPending={teamPending}
          oldestWaitDays={oldestWaitDays}
          coveragePct={coveragePct}
          teamAvailable={teamAvailable}
          teamSize={teamSize}
          onRefresh={load}
          onNewRequest={() => navigate('/requests/new')}
          onQueue={() => navigate('/manager/vacation-requests')}
        />

        {error ? (
          <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}

        <AnnouncementAlertBanner announcements={anns} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
            gap: 1,
          }}
        >
          {kpis.map((kpi) => (
            <ManagerKpiTile key={kpi.label} {...kpi} />
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.5fr) minmax(320px, 0.5fr)' },
            gap: 1.4,
            alignItems: 'start',
          }}
        >
          <Stack spacing={1.4} sx={{ minWidth: 0 }}>
            <ManagerPanel title="This week — team availability" subtitle="Confirmed coverage before you approve more leave.">
              <TeamWeekStrip week={teamWeek} teamSize={teamSize} />
            </ManagerPanel>

            <ManagerPanel
              title="Needs your decision"
              subtitle="Leave requests and pre-approvals waiting on you."
              action={
                <Button size="small" onClick={() => navigate('/manager/vacation-requests')}>
                  View all
                </Button>
              }
            >
              {teamPendingList.length || preApprovals.length ? (
                <Stack spacing={0.9}>
                  {teamPendingList.slice(0, 6).map((request) => (
                    <RequestReviewCard
                      key={request.id}
                      request={request}
                      overlaps={findOverlaps(request, teamRequests)}
                      busy={actingId === request.id}
                      onApprove={(row) => handleDecision(row, 'Approved')}
                      onReject={(row) => handleDecision(row, 'Rejected')}
                      onView={() => navigate('/manager/vacation-requests')}
                    />
                  ))}
                  {preApprovals.length > 0 && (
                    <>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mt: teamPendingList.length ? 0.5 : 0 }}
                      >
                        <Typography
                          variant="overline"
                          sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.5 }}
                        >
                          Pre-approvals
                        </Typography>
                        <Chip
                          size="small"
                          color="warning"
                          label={preApprovals.length}
                          sx={{ height: 18, fontWeight: 800 }}
                        />
                      </Stack>
                      {preApprovals.slice(0, 6).map((pa) => (
                        <Box
                          key={`pa-${pa.id}`}
                          sx={{
                            p: 1.1,
                            borderRadius: '12px',
                            border: (t) => `1px solid ${alpha(t.palette.warning.main, 0.3)}`,
                            bgcolor: (t) => alpha(t.palette.warning.main, 0.05),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>
                              {pa.candidate_name || pa.pre_approval_reference || `Pre-approval #${pa.id}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="div" noWrap>
                              {[pa.position_offered, pa.pre_approval_department_name].filter(Boolean).join(' · ') ||
                                pa.pre_approval_reference ||
                                'Pre-approval request'}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="contained"
                            color="warning"
                            onClick={() => navigate('/pre-approvals')}
                            sx={{ flexShrink: 0, borderRadius: '10px', fontWeight: 800, textTransform: 'none' }}
                          >
                            Review
                          </Button>
                        </Box>
                      ))}
                    </>
                  )}
                </Stack>
              ) : (
                <EmptyState compact title="All clear" description="Nothing is waiting on you." />
              )}
            </ManagerPanel>

            <ManagerPanel title="My recent requests" subtitle="Your own submissions, kept separate from the team queue.">
              <RecentRequestsTable rows={recentMyRequests} />
            </ManagerPanel>
          </Stack>

          <Stack spacing={1.4} sx={{ minWidth: 0 }}>
            <ManagerPanel title="Today's coverage" subtitle="Who's out and what's next.">
              <Stack spacing={1.1}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1 }}>
                      {teamSize ? `${teamAvailable}/${teamSize}` : teamAvailable}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750 }}>
                      Available team members
                    </Typography>
                  </Box>
                  <Chip size="small" label={`${coveragePct}% coverage`} sx={{ borderRadius: '8px', fontWeight: 850 }} />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={coveragePct}
                  sx={{ height: 7, borderRadius: 999, backgroundColor: alpha(theme.palette.warning.main, 0.1), '& .MuiLinearProgress-bar': { borderRadius: 999 } }}
                />
                {outTodayList.length ? (
                  <Stack spacing={0.6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750 }}>
                      Out today
                    </Typography>
                    {outTodayList.slice(0, 4).map((row) => (
                      <Stack key={row.id} direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                          {row.user?.name || row.employee_name || 'Team member'}
                        </Typography>
                        <Chip size="small" variant="outlined" label={getLeaveTypeLabel(row.leave_type)} sx={{ borderRadius: '8px' }} />
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Everyone is in today.
                  </Typography>
                )}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Next scheduled leave
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 850 }}>
                    {nextTeamLeave ? formatDateOnly(nextTeamLeave.start_date) : 'Not scheduled'}
                  </Typography>
                </Stack>
              </Stack>
            </ManagerPanel>

            <ManagerPanel title="Leave balances" subtitle="Your balances and projected year-end position.">
              <Stack spacing={0.8}>
                {balances.length ? (
                  balances.slice(0, 6).map((balance) => <BalanceRow key={balance.leave_type || balance.id} balance={balance} />)
                ) : (
                  <EmptyState compact title="No balances" description="No leave balances found." />
                )}
              </Stack>
            </ManagerPanel>

            <ManagerPanel title="Upcoming holidays" subtitle="Calendar signals that may affect approvals.">
              {upcomingHolidays.length ? (
                <Stack spacing={0.75}>
                  {upcomingHolidays.slice(0, 4).map((holiday) => (
                    <Stack
                      key={holiday.id || holiday.holiday_date || holiday.name}
                      direction="row"
                      spacing={1}
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ p: 0.9, borderRadius: '10px', border: '1px solid', borderColor: theme.hr?.line?.soft || 'divider' }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 850 }} noWrap>
                          {holiday.name || 'Holiday'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateOnly(holiday.holiday_date) || '-'}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={(() => {
                          const d = daysUntilDate(holiday.holiday_date);
                          if (d == null) return '-';
                          if (d === 0) return 'Today';
                          if (d === 1) return 'Tomorrow';
                          return `${d} d`;
                        })()}
                        sx={{ borderRadius: '8px' }}
                      />
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <EmptyState compact title="No upcoming holidays" description="No holiday records are scheduled." />
              )}
            </ManagerPanel>

            <ManagerPanel title="Announcements" subtitle="Active HR messages for your workspace.">
              {activeAnnouncements.length ? (
                <Stack spacing={0.75}>
                  {activeAnnouncements.slice(0, 3).map((announcement) => (
                    <Box
                      key={announcement.id || announcement.title}
                      sx={{ p: 0.9, borderRadius: '10px', border: '1px solid', borderColor: theme.hr?.line?.soft || 'divider', backgroundColor: alpha(theme.palette.info.main, 0.04) }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                        {announcement.title || 'Announcement'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateOnly(announcement.start_date || announcement.created_at) || 'Active'}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <EmptyState compact title="No active announcements" description="No active messages from HR." />
              )}
            </ManagerPanel>
          </Stack>
        </Box>
      </Stack>
    </PageLayout>
  );
}
